import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Loader2, MessageCircle, Send, Check, Edit3, X, Shield, Clock, ChevronDown
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = {
  admin: "bg-purple-50 text-purple-700 border-purple-200",
  borrower: "bg-blue-50 text-blue-700 border-blue-200",
  lender: "bg-emerald-50 text-emerald-700 border-emerald-200",
  investor: "bg-amber-50 text-amber-700 border-amber-200",
  originations: "bg-teal-50 text-teal-700 border-teal-200",
  underwriter: "bg-indigo-50 text-indigo-700 border-indigo-200",
  processor: "bg-teal-50 text-teal-700 border-teal-200",
  lender_relations: "bg-teal-50 text-teal-700 border-teal-200",
  investor_relations: "bg-teal-50 text-teal-700 border-teal-200",
};

const INTERNAL_ROLES = new Set(["admin", "originations", "underwriter", "processor", "lender_relations", "investor_relations"]);

function timeAgo(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function PendingBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
      <Clock className="h-2.5 w-2.5" /> Pending Review
    </span>
  );
}

function ModerationActions({ msg, leadId, onAction }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(msg.content);
  const [acting, setActing] = useState(false);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const handleRelease = async () => {
    setActing(true);
    try {
      const msgId = msg.msg_id || msg.message_id;
      await axios.post(`${API}/deals/${leadId}/messages/${msgId}/release`, {}, { headers });
      toast.success("Message released");
      onAction();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to release");
    } finally { setActing(false); }
  };

  const handleEditRelease = async () => {
    if (!editText.trim()) return;
    setActing(true);
    try {
      const msgId = msg.msg_id || msg.message_id;
      await axios.put(`${API}/deals/${leadId}/messages/${msgId}/edit`, { content: editText.trim() }, { headers });
      toast.success("Message edited & released");
      setEditing(false);
      onAction();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to edit");
    } finally { setActing(false); }
  };

  if (editing) {
    return (
      <div className="mt-2 space-y-2">
        <textarea
          value={editText}
          onChange={e => setEditText(e.target.value)}
          rows={2}
          className="w-full resize-none rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm text-[#333] focus:outline-none focus:border-emerald-300 focus:ring-1 focus:ring-emerald-100"
          data-testid="edit-message-textarea"
        />
        <div className="flex items-center gap-2">
          <button onClick={handleEditRelease} disabled={acting || !editText.trim()}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 transition-colors"
            data-testid="save-edit-release-btn">
            {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            Edit & Release
          </button>
          <button onClick={() => { setEditing(false); setEditText(msg.content); }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            data-testid="cancel-edit-btn">
            <X className="h-3 w-3" /> Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 mt-2">
      <button onClick={handleRelease} disabled={acting}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 transition-colors"
        data-testid="release-message-btn">
        {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
        Approve
      </button>
      <button onClick={() => setEditing(true)}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
        data-testid="edit-message-btn">
        <Edit3 className="h-3 w-3" /> Edit & Release
      </button>
    </div>
  );
}

export default function Deal360Messages({ leadId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const bottomRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };
  const isInternal = user && INTERNAL_ROLES.has(user.role);

  const fetchMessages = () => {
    axios.get(`${API}/deals/${leadId}/messages`, { headers })
      .then(r => setMessages(r.data?.messages || r.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 15000);
    return () => clearInterval(interval);
  }, [leadId]);

  useEffect(() => {
    // Only scroll within the messages container, not the whole page
    if (expanded && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, expanded]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/deals/${leadId}/messages`, { content: text.trim() }, { headers });
      setText("");
      fetchMessages();
    } catch (err) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Build lender letter map
  const lenderMap = {};
  let lenderIdx = 0;
  messages.forEach(msg => {
    const mRole = msg.user_role || msg.sender_role;
    const mId = msg.user_id || msg.sender_id;
    if (mRole === "lender" && mId && !lenderMap[mId]) {
      lenderMap[mId] = String.fromCharCode(65 + lenderIdx);
      lenderIdx++;
    }
  });

  const pendingCount = messages.filter(m => {
    const extra = m.extra || {};
    return extra.status === "pending_review";
  }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div data-testid="deal360-messages">
      {/* Header - always visible, clickable to expand/collapse */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-3 flex items-center justify-between hover:bg-[#FDFCF9] transition-colors"
        data-testid="messages-toggle"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 border border-emerald-200 flex items-center justify-center">
            <MessageCircle className="h-4 w-4 text-emerald-700" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-bold text-[#0A0A0A] tracking-tight">Deal Messages</h3>
            <p className="text-[10px] text-[#999]">{messages.length} message{messages.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isInternal && pendingCount > 0 && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200" data-testid="pending-review-count">
              <Shield className="h-3 w-3" />
              {pendingCount} pending
            </span>
          )}
          <ChevronDown className={`h-4 w-4 text-[#999] transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
        </div>
      </button>

      {expanded && (
        <>
          {/* Moderation notice for non-internal */}
          {!isInternal && (
        <div className="px-6 py-2 bg-[#FBF8F1] border-b border-[#f0ece4]">
          <p className="text-[10px] text-[#999] flex items-center gap-1.5">
            <Shield className="h-3 w-3 text-[#bbb]" />
            Messages are reviewed by our team before being shared with other parties
          </p>
        </div>
      )}

      {/* Messages */}
      <div ref={messagesContainerRef} className="h-[250px] overflow-y-auto px-6 py-4 space-y-4 bg-[#FDFCF9]" data-testid="messages-list">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageCircle className="h-10 w-10 text-[#ddd] mb-3" />
            <p className="text-sm text-[#999] font-medium">No messages yet</p>
            <p className="text-xs text-[#bbb] mt-1">Start a conversation about this deal</p>
          </div>
        ) : (
          messages.map((msg, i) => {
            const senderId = msg.user_id || msg.sender_id;
            const senderName = msg.user_name || msg.sender_name || "Unknown";
            const senderRole = msg.user_role || msg.sender_role || "";
            const isMe = senderId === user?.user_id;
            const roleCls = ROLE_COLORS[senderRole] || "bg-gray-50 text-gray-700 border-gray-200";
            const extra = msg.extra || {};
            const status = extra.status || "released";
            const isPending = status === "pending_review";

            let displayLabel;
            if (INTERNAL_ROLES.has(senderRole)) {
              displayLabel = "Internal";
            } else if (senderRole === "borrower") {
              displayLabel = "Borrower";
            } else if (senderRole === "lender") {
              const letter = lenderMap[senderId];
              displayLabel = letter ? `Lender ${letter}` : senderName;
            } else {
              displayLabel = senderName;
            }

            return (
              <div key={msg.msg_id || msg.message_id || i}
                className={`flex ${isMe ? "justify-end" : "justify-start"} ${isPending && isInternal ? "relative" : ""}`}
                data-testid={`message-${i}`}>

                {/* Pending highlight strip for internal users */}
                {isPending && isInternal && (
                  <div className="absolute -left-2 top-0 bottom-0 w-1 rounded-full bg-amber-400" />
                )}

                <div className={`max-w-[80%] ${isMe ? "items-end" : "items-start"}`}>
                  {/* Sender info */}
                  <div className={`flex items-center gap-1.5 mb-1 flex-wrap ${isMe ? "justify-end" : ""}`}>
                    <span className="text-[10px] font-semibold text-[#555]">{displayLabel}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-bold capitalize ${roleCls}`}>
                      {INTERNAL_ROLES.has(senderRole) ? "internal" : senderRole}
                    </span>
                    <span className="text-[10px] text-[#bbb]">{timeAgo(msg.created_at)}</span>
                    {isPending && isMe && !isInternal && <PendingBadge />}
                  </div>

                  {/* Bubble */}
                  <div className={`
                    px-4 py-2.5 rounded-2xl text-sm leading-relaxed
                    ${isPending && isInternal
                      ? "bg-amber-50 border-2 border-amber-200 text-[#333] rounded-bl-md shadow-sm"
                      : isMe
                        ? "bg-emerald-700 text-white rounded-br-md"
                        : "bg-white border border-[#EDE8DC] text-[#333] rounded-bl-md shadow-sm"
                    }
                  `}>
                    {msg.content}
                    {extra.edited_content && isInternal && (
                      <div className="mt-1 text-[10px] text-amber-600 flex items-center gap-1">
                        <Edit3 className="h-2.5 w-2.5" /> Edited before release
                      </div>
                    )}
                  </div>

                  {/* Moderation actions for internal on pending messages */}
                  {isPending && isInternal && (
                    <ModerationActions msg={msg} leadId={leadId} onAction={fetchMessages} />
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-6 py-3 border-t border-[#f0ece4] bg-white">
        <div className="flex items-end gap-3">
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            className="flex-1 resize-none rounded-xl border border-[#EDE8DC] bg-[#FDFCF9] px-4 py-2.5 text-sm text-[#333] placeholder-[#bbb]
              focus:outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 transition-all"
            data-testid="message-input"
          />
          <button
            onClick={handleSend}
            disabled={!text.trim() || sending}
            className="h-10 w-10 rounded-xl bg-emerald-700 text-white flex items-center justify-center hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors active:scale-95 shrink-0"
            data-testid="send-message-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
        <p className="text-[10px] text-[#bbb] mt-1.5">
          {isInternal ? "Your messages are sent directly. External messages require your review." : "Press Enter to send. Messages are reviewed before delivery."}
        </p>
      </div>
        </>
      )}
    </div>
  );
}
